(function(global) {
  "use strict";

  var EPSILON = 0.000001;
  var PRECISION_STEP = 0.01;
  var MAX_AUTOMATED_PIECES = 4;
  var MAX_AUTOMATED_TOTAL_JOINS = 2;
  var MAX_AUTOMATED_CROSS_JOINS = 1;
  var SUPPORTED_MATCH_TYPES = {
    none: true,
    straight: true,
    self_match: true
  };

  function toNumber(value) {
    if (value === null || value === undefined || value === "") return null;
    var number = Number(String(value).trim().replace(",", "."));
    return Number.isFinite(number) ? number : null;
  }

  function round(value, places) {
    var factor = Math.pow(10, places || 4);
    return Math.round((value + Number.EPSILON) * factor) / factor;
  }

  function ceilToStep(value, step) {
    if (!step || step <= 0) return round(Math.ceil((value - EPSILON) / PRECISION_STEP) * PRECISION_STEP, 4);
    return round(Math.ceil((value - EPSILON) / step) * step, 4);
  }

  function phase(value, repeat) {
    if (!repeat || repeat <= 0) return 0;
    var modulo = value % repeat;
    return round(modulo < EPSILON || repeat - modulo < EPSILON ? 0 : modulo, 4);
  }

  function phasesMatch(a, b, repeat) {
    if (!repeat || repeat <= 0) return true;
    var diff = Math.abs(phase(a, repeat) - phase(b, repeat));
    return diff < 0.005 || Math.abs(diff - repeat) < 0.005;
  }

  function isMultiple(value, step) {
    if (!step || step <= 0) return true;
    return phasesMatch(value, 0, step);
  }

  function normalizePatternClass(value) {
    return String(value || "unknown").trim().toLowerCase().replace(/\s+/g, "_");
  }

  function normalizeMatchType(value) {
    return String(value || "unknown").trim().toLowerCase().replace(/[\s-]+/g, "_");
  }

  function safe(reason, message, supportingMessage) {
    return {
      state: "professional_confirmation",
      reason: reason,
      message: message || "Final carpet layout requires professional confirmation",
      supportingMessage: supportingMessage || "Arra Flooring will confirm roll direction and joins before the carpet is ordered.",
      candidates: []
    };
  }

  function getOrientations(length, width, preference) {
    if (preference === "length") {
      return [{ key: "length", label: "Run pile along room length", requiredLength: length, crossWidth: width }];
    }
    if (preference === "width") {
      return [{ key: "width", label: "Run pile along room width", requiredLength: width, crossWidth: length }];
    }
    return [
      { key: "length", label: "Run pile along room length", requiredLength: length, crossWidth: width },
      { key: "width", label: "Run pile along room width", requiredLength: width, crossWidth: length }
    ];
  }

  function compatibleStripSections(rollWidth, stripWidth, acrossRepeat, seamPosition) {
    if (!acrossRepeat || acrossRepeat <= 0) {
      return Math.floor((rollWidth + EPSILON) / stripWidth);
    }

    var targetPhase = phase(seamPosition, acrossRepeat);
    var starts = [];
    var maxStart = rollWidth - stripWidth;
    var maxSteps = Math.ceil(rollWidth / acrossRepeat) + 2;

    for (var index = -1; index <= maxSteps; index += 1) {
      var start = targetPhase + index * acrossRepeat;
      if (start >= -EPSILON && start <= maxStart + EPSILON) starts.push(round(Math.max(0, start), 4));
    }

    starts = starts.sort(function(a, b) { return a - b; });
    var count = 0;
    var lastEnd = -Infinity;
    starts.forEach(function(start) {
      if (start + EPSILON >= lastEnd) {
        count += 1;
        lastEnd = start + stripWidth;
      }
    });
    return count;
  }

  function generateCandidate(room, rollWidth, orientation, pattern) {
    var lengthWithAllowance = room.allowancePercent > 0
      ? orientation.requiredLength * (1 + room.allowancePercent / 100)
      : orientation.requiredLength;
    var matchedMainLength = pattern.usesPattern
      ? ceilToStep(lengthWithAllowance, pattern.downRepeat)
      : ceilToStep(lengthWithAllowance, PRECISION_STEP);
    var crossWidth = orientation.crossWidth;
    var fullWidthPieces = Math.floor((crossWidth + EPSILON) / rollWidth);
    var stripWidth = round(crossWidth - fullWidthPieces * rollWidth, 4);

    if (fullWidthPieces < 1) {
      fullWidthPieces = 1;
      stripWidth = 0;
    }

    if (Math.abs(stripWidth) < EPSILON) stripWidth = 0;
    if (stripWidth < 0) return null;

    if (pattern.usesPattern && fullWidthPieces > 1 && !isMultiple(rollWidth, pattern.acrossRepeat)) {
      return null;
    }

    var pieces = [];
    var totalLinearMetres = 0;
    var patternAllowance = 0;

    for (var pieceIndex = 0; pieceIndex < fullWidthPieces; pieceIndex += 1) {
      pieces.push({
        type: pieceIndex === 0 ? "main_piece" : "full_width_piece",
        width: rollWidth,
        length: matchedMainLength,
        preservesPileDirection: true
      });
      totalLinearMetres += matchedMainLength;
      if (pattern.usesPattern) patternAllowance += Math.max(0, matchedMainLength - lengthWithAllowance);
    }

    var sideSections = 0;
    var sideCutLength = 0;
    var crossJoins = 0;

    if (stripWidth > EPSILON) {
      var usableSections = compatibleStripSections(rollWidth, stripWidth, pattern.usesPattern ? pattern.acrossRepeat : null, fullWidthPieces * rollWidth);
      if (usableSections < 1) return null;

      var rawSectionLength = lengthWithAllowance / usableSections;
      sideCutLength = pattern.usesPattern
        ? ceilToStep(rawSectionLength, pattern.downRepeat)
        : ceilToStep(rawSectionLength, PRECISION_STEP);
      sideSections = Math.ceil((lengthWithAllowance - EPSILON) / sideCutLength);

      if (sideSections < 1 || sideSections > usableSections) return null;

      for (var sectionIndex = 0; sectionIndex < sideSections; sectionIndex += 1) {
        pieces.push({
          type: "side_strip_section",
          width: stripWidth,
          length: sideCutLength,
          preservesPileDirection: true
        });
      }

      totalLinearMetres += sideCutLength;
      crossJoins = Math.max(0, sideSections - 1);
      if (pattern.usesPattern) patternAllowance += Math.max(0, sideSections * sideCutLength - lengthWithAllowance);
    }

    var parallelPieces = fullWidthPieces + (stripWidth > EPSILON ? 1 : 0);
    var longJoins = Math.max(0, parallelPieces - 1);
    var totalJoins = longJoins + crossJoins;
    var roomArea = round(room.length * room.width, 4);
    var suppliedArea = round(totalLinearMetres * rollWidth, 4);
    var estimatedWaste = round(Math.max(0, suppliedArea - roomArea), 4);

    return {
      state: "ok",
      rollWidth: round(rollWidth, 4),
      pileDirection: orientation.label,
      orientationKey: orientation.key,
      totalLinearMetres: round(totalLinearMetres, 4),
      suppliedArea: suppliedArea,
      roomArea: roomArea,
      estimatedWaste: estimatedWaste,
      patternAllowance: round(patternAllowance, 4),
      longJoins: longJoins,
      crossJoins: crossJoins,
      totalJoins: totalJoins,
      totalPieces: pieces.length,
      physicalCuts: pieces.length,
      layoutType: totalJoins > 0 ? "Joined layout" : "One-piece layout",
      professionalConfirmation: "Quantity estimate only - Arra Flooring will confirm the final order quantity.",
      pieces: pieces
    };
  }

  function rankCandidates(a, b) {
    if (a.totalJoins !== b.totalJoins) return a.totalJoins - b.totalJoins;
    if (a.crossJoins !== b.crossJoins) return a.crossJoins - b.crossJoins;
    if (a.physicalCuts !== b.physicalCuts) return a.physicalCuts - b.physicalCuts;
    if (a.suppliedArea !== b.suppliedArea) return a.suppliedArea - b.suppliedArea;
    if (a.estimatedWaste !== b.estimatedWaste) return a.estimatedWaste - b.estimatedWaste;
    if (a.totalLinearMetres !== b.totalLinearMetres) return a.totalLinearMetres - b.totalLinearMetres;
    return a.rollWidth - b.rollWidth;
  }

  function isSuitableForAutomatedRecommendation(candidate) {
    return candidate
      && candidate.physicalCuts <= MAX_AUTOMATED_PIECES
      && candidate.totalJoins <= MAX_AUTOMATED_TOTAL_JOINS
      && candidate.crossJoins <= MAX_AUTOMATED_CROSS_JOINS;
  }

  function calculate(input) {
    var length = toNumber(input && input.length);
    var width = toNumber(input && input.width);
    var allowancePercent = toNumber(input && input.allowancePercent);
    var rollWidths = Array.isArray(input && input.rollWidths) ? input.rollWidths.map(toNumber).filter(function(value) {
      return value && value > 0;
    }) : [];
    // Dave Grace approval 2026-08-05: all carpets use plain-carpet layout logic
    // until a future exception is explicitly approved. Pattern metadata is ignored.
    var patternClass = "plain";
    var matchType = "none";
    var downRepeat = null;
    var acrossRepeat = null;
    var preference = String(input && input.pileDirection || "compare");

    if (!length || !width || length <= 0 || width <= 0) return safe("invalid_dimensions", "Enter room length and width in metres.");
    if (length > 100 || width > 100) return safe("unreasonable_dimensions", "Room dimensions look unusually large. Ask Dave before calculating.");
    if (!rollWidths.length) return safe("missing_roll_widths", "Roll width is not available online for this product yet.");
    if (allowancePercent === null) allowancePercent = 0;
    if (allowancePercent < 0 || allowancePercent > 50) return safe("invalid_allowance", "Use a fitting allowance of 50% or less, or ask Dave for advice.");

    var usesPattern = false;

    var pattern = {
      usesPattern: usesPattern,
      matchType: matchType,
      downRepeat: downRepeat,
      acrossRepeat: acrossRepeat
    };
    var room = { length: length, width: width, allowancePercent: allowancePercent };
    var orientations = getOrientations(length, width, preference);
    var candidates = [];

    rollWidths.forEach(function(rollWidth) {
      orientations.forEach(function(orientation) {
        var candidate = generateCandidate(room, rollWidth, orientation, pattern);
        if (isSuitableForAutomatedRecommendation(candidate)) candidates.push(candidate);
      });
    });

    if (!candidates.length) return safe("excessive_or_no_valid_layout", "This room requires a manual layout assessment because no simple, reliable cutting plan was found.");

    candidates.sort(rankCandidates);
    return {
      state: "ok",
      recommended: candidates[0],
      alternatives: candidates.slice(1, 4),
      candidates: candidates
    };
  }

  var api = {
    calculate: calculate,
    _private: {
      ceilToStep: ceilToStep,
      compatibleStripSections: compatibleStripSections,
      isSuitableForAutomatedRecommendation: isSuitableForAutomatedRecommendation
    }
  };

  global.ArraCarpetLayoutEngine = api;
  if (typeof global.dispatchEvent === "function" && typeof global.Event === "function") {
    global.dispatchEvent(new global.Event("af:carpet-engine-ready"));
  }
  if (typeof module !== "undefined" && module.exports) module.exports = api;
})(typeof window !== "undefined" ? window : globalThis);
